// The client-side route decision, as a pure function.
//
// It lives here rather than inline in app.ts so it can be tested directly: who is
// let onto which page is an authorization-shaped question, and app.ts is a module
// of boot side effects that no test can import. The rendering half — turning
// `forbidden` into the actual ErrorScreen inside the app shell — stays in app.ts,
// which is what keeps this file free of component imports.
//
// Advisory only, like the guard it came from: the server re-authorizes every
// protected call. This never grants access, it only avoids dead ends.

export type GuardDecision =
  | { kind: 'allow' }
  | { kind: 'redirect'; to: string }
  | { kind: 'forbidden' };

/** Pages reachable without a session. */
export const PUBLIC_PATHS: ReadonlySet<string> = new Set([
  '/login',
  '/auth/verify',
  '/recovery',
  '/invite',
  // The driver trip-mode surface (GPS-011): entered from a trip link whose
  // credential is a trip-scoped tracking token — there is no app session
  // on this path by design, and no app API behind it.
  '/trip',
]);

/**
 * Routes that must boot with NO session machinery at all (GPS-011).
 *
 * Distinct from PUBLIC_PATHS: those are merely reachable without a session —
 * they still boot through authInit, the forced-reauth rewrite, and the company
 * picker, because a visitor to /login or /invite is *becoming* a session. The
 * driver trip route is different in kind: its only credential is a trip-scoped
 * token in the URL fragment and it calls the tracking service directly, never an
 * app API. Running the session bootstrap there is not just wasted work, it is
 * destructive — the forced-reauth rewrite replaces the URL with /login and takes
 * the fragment token with it, which is exactly how a phone carrying any prior
 * Fexo state lost its trip link (found in live verification, app#14).
 *
 * app.ts consults this before the session bootstrap; the routing decision itself
 * still runs normally (these paths are in PUBLIC_PATHS too).
 */
export const SESSION_LESS_PATHS: ReadonlySet<string> = new Set(['/trip']);

/** Whether `path` must skip the session bootstrap entirely. */
export function isSessionLessRoute(path: string): boolean {
  return SESSION_LESS_PATHS.has(path);
}

/**
 * Whether boot should run the session-establishing network probes at all
 * (issue 444).
 *
 * Every non-session-less boot used to call authInit() unconditionally, which
 * fires GET /api/me then, on its 401, POST /api/auth/refresh — both fail on a
 * browser that has no session, so a fully anonymous page view (a fresh visit to
 * /login, /recovery, an unknown path, …) paid two logged 401s for nothing.
 *
 * The gate is a signed-OUT flag (auth.ts's isExplicitlySignedOut /
 * markExplicitlySignedOut lifecycle), not the route: /login and /recovery
 * must keep running the bootstrap whenever a session might exist, because a
 * signed-in visitor landing there still has to be redirected home (issue 299
 * — see SIGNED_IN_FORBIDDEN_PATHS above), which needs isAuthenticated() to be
 * truthful on exactly those routes. A route-based skip would make that check
 * a lie.
 *
 * Polarity is deliberately "attempt UNLESS explicitly signed out", not
 * "attempt only if a signed-in hint is present" (redesigned per Valentin's
 * PR 471 review, finding 2): the original signed-IN-hint design failed
 * CLOSED — deploy day, Safari ITP's 7-day storage eviction, and a
 * quota-full store all land on "valid refresh cookie, hint absent, treated
 * as signed out", stranding a browser with a perfectly live session. Gating
 * on the ABSENCE of an explicit, definitive signed-out signal instead fails
 * OPEN in every one of those cases — never-set, evicted, or failed-to-write
 * all read as "attempt", which is the safe direction. The accepted trade-off:
 * a browser that has never signed in also reads as "attempt" (it has no flag
 * either), so it still pays one 401'ing probe pass per visit.
 *
 * The one override: an OAuth callback landing (`?auth=success`). This
 * matters even under the new polarity for a browser that explicitly signed
 * out and is now completing a FRESH sign-in: the stale signed-out flag from
 * the earlier logout has not been cleared yet (that only happens once the
 * bootstrap this override authorizes actually succeeds — see
 * setAccessToken), so without the override that one boot would wrongly skip.
 */
export function shouldBootstrapSession(explicitlySignedOut: boolean, isOAuthSuccessLanding: boolean): boolean {
  return !explicitlySignedOut || isOAuthSuccessLanding;
}

/**
 * Pages a signed-in user has no business on (issue 299).
 *
 * Deliberately NOT every public path. /auth/verify and /invite stay reachable
 * with a session on purpose: clicking a *new* magic link is how someone switches
 * account or device, and accepting an invite to another company is a normal
 * multi-company flow. A blanket "authenticated ⇒ off the public pages" breaks
 * both, which is why this is an explicit list rather than the complement of one.
 */
export const SIGNED_IN_FORBIDDEN_PATHS: ReadonlySet<string> = new Set(['/login', '/recovery']);

/**
 * Decide what should happen for `path`.
 *
 * `authed` and `canView` are passed in rather than imported so this stays pure —
 * the caller owns the session and RBAC lookups.
 */
export function guardDecision(
  path: string,
  authed: boolean,
  canView: (p: string) => boolean,
): GuardDecision {
  // Checked BEFORE the public allow: the public list used to short-circuit
  // first, which is exactly how a signed-in user reached the full sign-in card
  // — via Back after signing in, or a stale bookmark — and could mail themselves
  // a fresh magic link from it (issue 299).
  if (authed && SIGNED_IN_FORBIDDEN_PATHS.has(path)) return { kind: 'redirect', to: '/' };

  if (PUBLIC_PATHS.has(path)) return { kind: 'allow' };
  if (!authed) return { kind: 'redirect', to: '/login' };

  // Authenticated: mirror the server RBAC policy so a module this user's role +
  // device class can't reach lands on the friendly forbidden screen in place,
  // rather than a dead nav target or an empty shell.
  if (!canView(path)) return { kind: 'forbidden' };

  return { kind: 'allow' };
}
